import type { SessionData } from '~/lib/session-query'
import { Dropdown, Label, Surface } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Activity, ChevronDown, LogOut, Settings } from 'lucide-react'
import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { LocaleSwitcher } from '~/components/common/locale-switcher'
import { LocalizedLink } from '~/components/common/localized-link'
import { ModeToggle } from '~/components/common/mode-toggle'
import { AUTH_ROUTES, getAuthRoute } from '~/lib/auth/routes'
import { footballCoreHref } from '~/lib/navigation/football-core'
import { createSessionQuery } from '~/lib/session-query'

export function Header() {
  const t = useTranslations('components.layout.header')
  const locale = useLocale()
  const { data } = useQuery(createSessionQuery())
  const isLoggedIn = !!data?.session
  const signInHref = footballCoreHref(locale, getAuthRoute(AUTH_ROUTES.signIn))
  const signUpHref = footballCoreHref(locale, getAuthRoute(AUTH_ROUTES.signUp))

  return (
    <Surface variant="default" className="sticky top-0 z-50 w-full border-b border-divider/50 backdrop-blur-md bg-background/80">
      <div className="container mx-auto grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-1 gap-y-1 px-2 py-2 sm:gap-x-3 sm:px-4 lg:px-6">
        <LocalizedLink
          to="/matches"
          className="inline-flex min-h-11 min-w-0 items-center gap-2 rounded-xl font-bold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Activity className="hidden size-5 shrink-0 text-primary sm:block" aria-hidden="true" />
          <span className="truncate text-lg" translate="no">BallPeek</span>
        </LocalizedLink>
        <div className="flex min-w-0 shrink-0 items-center justify-end gap-1 sm:gap-2">
          <span className="hidden sm:inline-flex"><ModeToggle /></span>
          <LocaleSwitcher />
          {isLoggedIn
            ? <AccountMenu user={data?.user} />
            : (
                <>
                  <a
                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-2 text-xs font-semibold text-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
                    href={signInHref}
                  >
                    {t('signIn')}
                  </a>
                  <a
                    className="hidden min-h-11 items-center rounded-full bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:inline-flex"
                    href={signUpHref}
                  >
                    {t('getStarted')}
                  </a>
                </>
              )}
        </div>
        <nav aria-label={t('primaryNavigation')} className="col-span-2 grid min-w-0 grid-cols-2 gap-2 border-t border-divider/40 pt-1">
          <LocalizedLink
            to="/matches"
            className="inline-flex min-h-11 min-w-0 items-center justify-center rounded-xl px-2 text-center text-sm font-semibold text-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {t('matches')}
          </LocalizedLink>
          <LocalizedLink
            to="/teams-players"
            className="inline-flex min-h-11 min-w-0 items-center justify-center rounded-xl px-2 text-center text-sm font-semibold text-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {t('teamsPlayers')}
          </LocalizedLink>
        </nav>
      </div>
    </Surface>
  )
}

function AccountMenu({ user }: { user: SessionData['user'] }) {
  const t = useTranslations('components.layout.header')
  const displayName = user?.name || user?.email || t('unknownUser')
  const email = user?.email || t('unknownUser')
  const initials = String(displayName).trim().slice(0, 2).toUpperCase() || 'BP'

  return (
    <Dropdown>
      <Dropdown.Trigger
        aria-label={t('accountMenu')}
        className="button button--md button--ghost min-h-11 max-w-[13rem] gap-2 rounded-full border border-divider bg-default-50 px-2.5 text-left text-sm font-semibold text-foreground hover:bg-default-100 md:max-w-[17rem]"
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background">
          {initials}
        </span>
        <span className="hidden min-w-0 flex-col leading-tight sm:flex">
          <span className="truncate">{email}</span>
        </span>
        <ChevronDown className="hidden size-4 shrink-0 text-muted sm:block" aria-hidden="true" />
      </Dropdown.Trigger>
      <Dropdown.Popover placement="bottom end" className="min-w-64">
        <div className="border-b border-divider px-3 py-3">
          <p className="text-xs font-semibold uppercase text-muted">{t('signedInAs')}</p>
          <p className="mt-1 truncate text-sm font-semibold text-foreground">{displayName}</p>
          <p className="truncate text-xs text-muted">{email}</p>
        </div>
        <Dropdown.Menu aria-label={t('accountMenu')}>
          <Dropdown.Item key="settings" id="settings" textValue={t('settings')} href="/settings/account">
            <div className="flex items-center gap-2">
              <Settings className="size-4 text-muted" aria-hidden="true" />
              <Label>{t('settings')}</Label>
            </div>
          </Dropdown.Item>
          <Dropdown.Item key="sign-out" id="sign-out" textValue={t('signOut')} href="/auth/sign-out">
            <div className="flex items-center gap-2 text-danger">
              <LogOut className="size-4" aria-hidden="true" />
              <Label>{t('signOut')}</Label>
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}
